import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Rate } from '@util/firestore/shipengine';
import { getProductRates } from '@util/firestore/shipengine/shipengine.service';
import { logError } from '@util/logError';
import { AddressDocument } from '@util/types/firestore/address';
import { ProductDocument } from '@util/types/firestore/products';
import {
  calculateShipping,
  useShoppingCart,
} from 'context/ShoppingCartContext';
import { useState, useMemo, useEffect, useRef } from 'react';
import { formatCurrency } from '..';

const RATE_DISPLAY_LIMIT = 3;

function isPopularRate(rate: Rate) {
  const isInternational = rate.service_code?.includes('international');
  const service_codes = isInternational
    ? [
        'usps_priority_mail_international',
        'ups_standard_international',
        'fedex_international_economy',
      ]
    : ['usps_priority_mail', 'ups_ground', 'fedex_home_delivery'];
  return service_codes.includes(rate.service_code) ? 1 : 0;
}

function sortRates(rates: Rate[]) {
  return [...rates].sort(
    (a, b) =>
      isPopularRate(b) - isPopularRate(a) || a.delivery_days - b.delivery_days
  );
}

/** Cache key for per-product rate queries via our API route */
function rateKey(productId: string, addressLine1: string) {
  return ['productRates', productId, addressLine1];
}

/**
 * Prefetch shipping rates for products in the background.
 * Call this from the Information step so rates are cached
 * before the user reaches the Shipping step.
 */
export function usePrefetchRates() {
  const queryClient = useQueryClient();

  return (products: ProductDocument[], shipTo: AddressDocument) => {
    // GB #1527: a debug line here logged `shipTo.address_line1` — the buyer's
    // street address in plaintext, to the production browser console, on every
    // prefetch pass. Removed. Nothing diagnostic is lost: the rate query is keyed
    // by address_line1, so it is visible in React Query devtools, and rate
    // failures now report to Sentry with the address redacted.
    //
    // GB #1527: `prefetchQuery` is `fetchQuery().then(noop).catch(noop)` in
    // query-core, so every rejection here was discarded. Prefetch is the
    // EARLIEST point a bad ship_to shows up — a draft address missing a phone
    // 400s all of them at once, on the Information step, before the user ever
    // reaches Shipping — and it reported nothing at all. Calling fetchQuery
    // directly and attaching our own catch keeps prefetch semantics exactly
    // (same cache write, no UI change, no unhandled rejection) while making the
    // failure visible.
    //
    // One report per prefetch pass: when the address is at fault every product
    // fails identically, and a 20-item cart should not send 20 copies of the
    // same diagnosis. Later failures still surface through useQuery's onError
    // when the Shipping step mounts.
    let reported = false;

    for (const product of products) {
      const skip = product.is_flat_rate === true || !product.shipment;
      console.info('[Prefetch]', product.id, { is_flat_rate: product.is_flat_rate, hasShipment: !!product.shipment, skip });
      if (skip) continue;

      queryClient
        .fetchQuery({
          queryKey: rateKey(product.id, shipTo.address_line1),
          queryFn: () =>
            getProductRates({
              product_id: product.id,
              price: product.price,
              categories: product.categories,
              shipment: product.shipment,
            }, shipTo).then(sortRates),
          staleTime: 1000 * 60 * 60 * 24,
          cacheTime: 1000 * 60 * 60 * 24, // keep in cache even when component unmounts
        })
        .catch((err) => {
          if (reported) return;
          reported = true;
          void logError(err);
        });
    }
  };
}

export const useRates = ({
  product,
  shipTo,
  chooseCheapestByDefault,
  useApiRoute = true,
}: {
  product: ProductDocument;
  shipTo: AddressDocument;
  chooseCheapestByDefault: boolean;
  /** When true, uses our Next.js API route instead of Cloud Function */
  useApiRoute?: boolean;
}) => {
  const { handleShippingRateSelected, selectedShippingRates } =
    useShoppingCart();
  const queryClient = useQueryClient();
  const [showAllRates, setShowAllRates] = useState<boolean>(false);

  // Fetch via our API route (checkout flow) — matches prefetch cache key
  const {
    data: apiRates,
    isLoading: apiLoading,
    error: apiError,
  } = useQuery({
    queryKey: rateKey(product.id, shipTo.address_line1),
    queryFn: () =>
      getProductRates({
        product_id: product.id,
        price: product.price,
        categories: product.categories,
        shipment: product.shipment,
      }, shipTo).then(sortRates),
    staleTime: 1000 * 60 * 60 * 24,
    cacheTime: 1000 * 60 * 60 * 24, // survive component unmount
    enabled: useApiRoute && !product.is_flat_rate,
    refetchOnWindowFocus: false,
    retry: 1,
    retryDelay: 2000,
    // Surface rate-fetch failures (silent before — this is why the offer-modal
    // outage was invisible in Sentry for ~18 days). GB #1313.
    onError: (err) => logError(err),
  });

  // The Cloud Function rate path (`getRates`) was removed in the checkout
  // migration (api commit c13c138). All rate fetching now goes through the
  // Next.js API route via getProductRates. GB #1313.
  const allRates = apiRates;
  const ratesLoading = !product.is_flat_rate && apiLoading;
  const ratesError = apiError;

  function retryRates() {
    queryClient.invalidateQueries(rateKey(product.id, shipTo.address_line1));
  }

  // partition the rates into popular and rest
  const [popular, rest] = useMemo(() => {
    return (
      allRates?.reduce(
        (acc, rate) => {
          if (isPopularRate(rate)) {
            acc[0].push(rate);
          } else {
            acc[1].push(rate);
          }
          return acc;
        },
        [[], []] as Rate[][]
      ) ?? [[], []]
    );
  }, [allRates]);

  // slice the rates to show the popular ones first, then the rest
  const rates = [...popular, ...rest].slice(
    0,
    showAllRates ? allRates?.length : RATE_DISPLAY_LIMIT
  );

  function selectOption(option: Rate) {
    handleShippingRateSelected(product.seller_id, product.id, option);
  }

  // for the current product, get the selected rate from the cart
  const selectedRateForProduct = selectedShippingRates
    .get(product.seller_id)
    ?.get(product.id);

  // find the selected rate in the rates response
  const foundRate =
    selectedRateForProduct &&
    rates?.find((r) => r.service_code === selectedRateForProduct.service_code);

  if (rates && !selectedRateForProduct && chooseCheapestByDefault) {
    const cheapestRate = rates?.sort(
      (a, b) => a.total_amount - b.total_amount
    )[0];
    if (cheapestRate) {
      setTimeout(() => selectOption(cheapestRate), 0);
    }
  }

  // When rates refetch (address changed), auto-select cheapest to prevent stale rate
  // (Kyle audit finding #5). Only fires when: (a) chooseCheapestByDefault is enabled,
  // (b) rates are loaded, and (c) the currently selected rate is not in the new set.
  const prevAllRatesRef = useRef<Rate[] | undefined>(undefined);
  useEffect(() => {
    if (!chooseCheapestByDefault) return;
    if (!allRates?.length) return;
    // Skip if the rates array reference hasn't changed (no refetch happened)
    if (allRates === prevAllRatesRef.current) return;
    prevAllRatesRef.current = allRates;

    // If there is a selected rate that no longer exists in the new rates,
    // it's stale (different address → different rate IDs). Re-select cheapest.
    if (selectedRateForProduct) {
      const isStale = !allRates.some(
        (r) => r.service_code === selectedRateForProduct.service_code
      );
      if (isStale) {
        const cheapest = [...allRates].sort(
          (a, b) => a.total_amount - b.total_amount
        )[0];
        if (cheapest) selectOption(cheapest);
      }
    }
  // selectOption is stable (calls context setter), allRates ref changes on refetch
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allRates]);

  const getFlatRateShipping = () => {
    const cost = calculateShipping(product, shipTo)?.cost;
    return formatCurrency(cost || 0);
  };

  return {
    allRates,
    rates,
    ratesLoading,
    ratesError,
    foundRate,
    getFlatRateShipping,
    selectOption,
    retryRates,
    showAllRates,
    setShowAllRates,
    isPopularRate,
    RATE_DISPLAY_LIMIT,
  };
};
